import React, { useMemo } from 'react';
import { Form } from 'react-final-form';
import { Box, Typography } from 'housecall-ui';
import makeStyles from 'housecall-ui/dist/DesignSystem/Styles/makeStyles';

import {
  calculatePricingFormTotalPrice,
  FormValues,
  generatePricingFormInitialValues,
} from '../../components/form/utils';
import { ScrollableContent } from '../../components/ScrollableContent';
import { useChangeStep } from '../../hooks/useChangeStep';
import { PricingFormValue } from '../../state/types';
import { useOnlineBookingReducer } from '../../state/useOnlineBookingReducer';
import { PricingFormType } from '../../types/types';
import { formatMoney } from '../../utils/formatMoney';
import { isPresent } from '../../utils/isPresent';
import { BackArrowButton } from '../BackArrowButton';

import { PricingFormFields } from './PricingFormFields';

const useStyles = makeStyles((theme) => ({
  imageClass: {
    display: 'block',
    maxHeight: '170px',
    maxWidth: '100%',
    borderRadius: '3px',
    marginBottom: theme.spacing(2),
  },
}));

type Props = {
  pricingForm: PricingFormType;
};

export const PricingFormContentComponent = ({ pricingForm }: Props) => {
  const {
    dispatch,
    actions,
    state: { pricingFormValues },
  } = useOnlineBookingReducer();
  const { changeStep } = useChangeStep();

  const { imageClass } = useStyles();

  const initialValues = useMemo(() => generatePricingFormInitialValues(pricingForm), [pricingForm]);
  const foundValues = pricingFormValues.find((item: PricingFormValue) => item.uuid === pricingForm?.uuid);

  const onChange = (formValues: FormValues) => {
    if (pricingForm?.uuid && formValues) {
      dispatch(actions.setPricingFormValues(pricingForm.uuid, formValues));
    }
  };

  const handleBackClick = () => {
    changeStep('categories');
  };

  return (
    <Form initialValues={foundValues?.values || initialValues} onSubmit={() => undefined}>
      {(props) => (
        <>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <BackArrowButton onClick={handleBackClick} />
            <Typography variant="h3" data-testid="pricing-form-total">
              {formatMoney(calculatePricingFormTotalPrice(pricingForm, props.values))}
            </Typography>
          </Box>
          <ScrollableContent>
            <Box mt={1} mb={2}>
              {isPresent(pricingForm.imageUrl) && (
                <img alt="price form" className={imageClass} src={pricingForm.imageUrl} />
              )}
              <Typography variant="h3" data-testid="pricing-form-name">
                {pricingForm.name}
              </Typography>
              {isPresent(pricingForm.description) ? (
                <Typography variant="body2" color="secondary">
                  {pricingForm.description}
                </Typography>
              ) : null}
            </Box>
            <form onSubmit={props.handleSubmit}>
              <PricingFormFields onChange={onChange} config={pricingForm as PricingFormType} />
            </form>
          </ScrollableContent>
        </>
      )}
    </Form>
  );
};
